import { Pencil } from "lucide-react";
import { useState } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  type AdminIntensityLevel,
  useAdminIntensityLevels,
} from "@/hooks/use-admin-intensity-levels";
import { useHoursPerIntensityPoint, useSetHoursPerIntensityPoint } from "@/hooks/use-system-config";
import { IntensityLevelEditDialog } from "./IntensityLevelEditDialog";

const INTENSITY_SCALE = [1, 2, 3, 4, 5, 6, 7, 8] as const;
const DEFAULT_HOURS_PER_POINT = 40;

export function IntensityLevelsManager() {
  const { data: levels = [], isLoading, isError, error, refetch } = useAdminIntensityLevels();
  const [editing, setEditing] = useState<AdminIntensityLevel | null>(null);

  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load intensity levels"}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        The Nymbl Staff system design fixes intensity at 8 levels. You can rename each level,
        document when it applies, and tune the color used on Solution cards and the schedule editor.
      </p>

      {isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {Array.from({ length: 8 }, (_, i) => `skeleton-${i + 1}`).map((key) => (
            <Skeleton key={key} className="h-24 w-full" />
          ))}
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {levels.map((level) => (
            <Card key={level.id} className="border-border/50">
              <CardContent className="flex items-start gap-4 p-4">
                <div
                  className="h-12 w-12 shrink-0 rounded-md border"
                  style={{ backgroundColor: level.color }}
                  role="img"
                  aria-label={`Color swatch ${level.color}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <div className="text-xs font-medium text-muted-foreground">
                        Level {level.level}
                      </div>
                      <div className="font-semibold truncate">{level.label}</div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setEditing(level)}
                      aria-label={`Edit Level ${level.level}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </div>
                  {level.description ? (
                    <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
                      {level.description}
                    </p>
                  ) : (
                    <p className="mt-1 text-xs text-muted-foreground/60 italic">No description</p>
                  )}
                  <p className="mt-2 font-mono text-[11px] text-muted-foreground">{level.color}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {editing && (
        <IntensityLevelEditDialog
          key={editing.id}
          open
          onOpenChange={(open) => !open && setEditing(null)}
          level={editing}
        />
      )}

      <div className="border-t pt-6">
        <HoursPerIntensityPointBenchmark />
      </div>
    </div>
  );
}

/**
 * Story 6 (Visualize Solution Workload): admin control for the global
 * hours-per-Intensity-point constant that powers the Solution workload
 * chart's benchmark overlay (Story 4). Display-only and linear — never
 * feeds Fit Score, Capacity, or Assignment points.
 */
function HoursPerIntensityPointBenchmark() {
  const { data, isLoading, isError, error, refetch } = useHoursPerIntensityPoint();
  const setHoursPerPoint = useSetHoursPerIntensityPoint();

  const savedValue = data?.value ?? DEFAULT_HOURS_PER_POINT;
  const [draft, setDraft] = useState(String(savedValue));

  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load the hours-per-Intensity-point benchmark"}
        onRetry={() => refetch()}
      />
    );
  }

  const parsedDraft = Number(draft);
  const isValidDraft = Number.isFinite(parsedDraft) && parsedDraft > 0;
  const previewValue = isValidDraft ? parsedDraft : savedValue;
  const isDirty = draft.trim() !== String(savedValue);

  const handleSave = () => {
    if (!isValidDraft) return;
    setHoursPerPoint.mutate(parsedDraft);
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-semibold">Hours-per-Intensity-point benchmark</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          A single global constant that converts a Solution's Intensity into an expected-hours
          benchmark:{" "}
          <span className="font-medium text-foreground">
            expected hours/month = Intensity × constant
          </span>
          . It powers the benchmark overlay on the Solution workload chart only — it is display-only
          and does not trigger a Fit Score recalculation, a Capacity recheck, or any change to
          Assignment points.
        </p>
      </div>

      {isLoading ? (
        <Skeleton className="h-24 w-full max-w-sm" />
      ) : (
        <Card className="border-border/50 max-w-sm">
          <CardContent className="space-y-3 p-4">
            <div className="space-y-2">
              <Label htmlFor="hours-per-intensity-point">Hours per Intensity point</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="hours-per-intensity-point"
                  type="number"
                  min={1}
                  step={1}
                  inputMode="numeric"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  aria-invalid={!isValidDraft}
                  className="max-w-[140px]"
                />
                <span className="text-sm text-muted-foreground">hrs / month at Level 1</span>
              </div>
              {!isValidDraft && (
                <p className="text-xs text-destructive">Enter a whole number greater than 0.</p>
              )}
            </div>
            <Button
              onClick={handleSave}
              disabled={!isValidDraft || !isDirty || setHoursPerPoint.isPending}
            >
              {setHoursPerPoint.isPending ? "Saving…" : "Save"}
            </Button>
          </CardContent>
        </Card>
      )}

      <div>
        <p className="text-xs font-medium text-muted-foreground">
          Preview — resulting benchmark across the scale
        </p>
        <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 max-w-md sm:grid-cols-4">
          {INTENSITY_SCALE.map((level) => (
            <div key={level} className="flex items-baseline justify-between gap-2 text-sm">
              <span className="text-muted-foreground">Level {level}</span>
              <span className="font-mono font-medium">
                {(level * previewValue).toLocaleString()} hrs
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
